 
/* eslint-disable @typescript-eslint/no-unused-vars */

import { AssemblyTemplateField, Maybe } from '@/__generated__/graphql';
import { Columns } from '@/types';

export const prepareTemplateFields = (allTemplateFields: AssemblyTemplateField[] | undefined) => {
  const existingFields: AssemblyTemplateField[] = [];
  const uniqueNewFields: AssemblyTemplateField[] = [];
  const duplicateNewFields: AssemblyTemplateField[] = [];

  const existingKeys = new Set<Maybe<string> | undefined>();
  const existingNames = new Set<string>();

  const fields =
    allTemplateFields?.map((templateField) => {
      const field = { ...templateField };
      delete field.__typename;
      if (field.displayConfig?.__typename) {
        const { __typename, ...rest } = field.displayConfig;
        if (rest.color) {
          rest.color = rest.color.map((color: any) =>
            JSON.parse(JSON.stringify(color), (key, value) => {
              return key === '__typename' ? undefined : value;
            })
          );
        }
        field.displayConfig = rest;
      }
      return field;
    }) ?? [];

  for (const field of allTemplateFields ?? []) {
    const isNewField = field.id?.includes?.('newField') ?? false;
    if (!isNewField) {
      existingFields.push(field);
      existingKeys.add(field.key);
      existingNames.add(field.name);
      continue;
    }

    const isDuplicate = existingKeys.has(field.key) || existingNames.has(field.name);
    (isDuplicate ? duplicateNewFields : uniqueNewFields).push(field);
  }

  // recursive cleaner for condition trees
  const cleanConditionTree = (node: any): any => {
    if (!node) return node;
    const cleanedNode = { ...node };
    delete cleanedNode.__typename;
    delete cleanedNode.id;
    if (cleanedNode.nodeType === null) delete cleanedNode.nodeType;
    if (cleanedNode.operator === null) delete cleanedNode.operator;
    if (cleanedNode.logicOperator === null) delete cleanedNode.logicOperator;
    if (cleanedNode.inputVariable === null) delete cleanedNode.inputVariable;
    if (cleanedNode.value === null) delete cleanedNode.value;
    if (cleanedNode.conditions && Array.isArray(cleanedNode.conditions)) {
      cleanedNode.conditions = cleanedNode.conditions.map(cleanConditionTree);
    }
    return cleanedNode;
  };

  const cleanField = (field: any) => {
    const cleaned = { ...field };
    delete cleaned.__typename;

    if (!cleaned.key && cleaned.name) {
      cleaned.key = cleaned.name.replace(/\s+/g, '_').toLowerCase();
    }

    if (cleaned.options && Array.isArray(cleaned.options)) {
      cleaned.options = cleaned.options
        .filter((option: string) => option && typeof option === 'string')
        .map((option: string) => option.trim());
    }

    if (cleaned.displayConfig?.__typename) {
      const { __typename, ...rest } = cleaned.displayConfig;
      if (rest.color) {
        rest.color = rest.color.map((color: any) =>
          JSON.parse(JSON.stringify(color), (key, value) => {
            return key === '__typename' ? undefined : value;
          })
        );
      }
      cleaned.displayConfig = rest;
    }

    if (cleaned.optionsData) {
      cleaned.optionsData = cleaned.optionsData.map((option: any) => {
        const { __typename, ...rest } = option;
        return {
          ...rest,
          weight: option.weight ?? 0,
        };
      });
    }

    if (cleaned.logicRule) {
      const { __typename, ...logicRuleRest } = cleaned.logicRule;
      if (!logicRuleRest.evaluationStrategy) {
        logicRuleRest.evaluationStrategy = 'FIRST_MATCH';
      }
      if (logicRuleRest.defaultValue === null) delete logicRuleRest.defaultValue;
      if (logicRuleRest.description === null) delete logicRuleRest.description;

      cleaned.logicRule = {
        ...logicRuleRest,
        variables: logicRuleRest.variables?.map((v: any) => {
          const { __typename, ...varRest } = v;
          if (varRest.templateType === null) delete varRest.templateType;
          if (varRest.isBlisterField === null) delete varRest.isBlisterField;
          return varRest;
        }),
        conditionalMappings: logicRuleRest.conditionalMappings?.map((m: any) => {
          const { __typename, id, ...mappingRest } = m;
          if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
          return {
            ...mappingRest,
            conditionTree: cleanConditionTree(mappingRest.conditionTree),
          };
        }),
      };
    }

    if (cleaned.calculatedBy) {
      const { __typename, ...calculatedByRest } = cleaned.calculatedBy;
      if (!calculatedByRest.evaluationStrategy) {
        calculatedByRest.evaluationStrategy = 'FIRST_MATCH';
      }
      if (calculatedByRest.defaultValue === null) delete calculatedByRest.defaultValue;
      if (calculatedByRest.description === null) delete calculatedByRest.description;

      cleaned.calculatedBy = {
        ...calculatedByRest,
        variables: calculatedByRest.variables?.map((v: any) => {
          const { __typename, ...varRest } = v;
          if (varRest.templateType === null) delete varRest.templateType;
          if (varRest.isBlisterField === null) delete varRest.isBlisterField;
          return varRest;
        }),
        conditionalMappings: calculatedByRest.conditionalMappings?.map((m: any) => {
          const { __typename, id, ...mappingRest } = m;
          if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
          return {
            ...mappingRest,
            conditionTree: cleanConditionTree(mappingRest.conditionTree),
          };
        }),
      };
    }

    return cleaned;
  };

  const cleanedFields = fields.map(cleanField);
  const preventPlannerDuplicateField = (field: Columns, allColumns: Columns[]): boolean => {
    return allColumns.some((column) => column.templateFieldKey === field.key);
  };
  return {
    cleanedFields,
    duplicateNewFields,
    preventPlannerDuplicateField,
    uniqueNewFields,
    existingFields,
  };
};
